/**
 * Workspace SPA root component with React Router 6 configuration,
 * responsive layout shell with navigation bar and content area.
 *
 * @module apps/workspace/App
 * @see TASK-FRONTEND-WS-SHELL-CHG-001
 */
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useRoutes, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { t } from "@lingui/core/macro";
import { VerisaThemeProvider } from "../../theme/ThemeProvider";
import {
  BrandingProvider,
  useBranding,
  type BrandingState,
} from "../../branding/BrandingProvider";
import { WorkspaceAuthWrapper } from "./AuthWrapper";
import { useAuth } from "../../store/AuthContext";
import { ImpersonationBannerSlot } from "../tenant/AuthWrapper";
import { impersonationIdentityKey } from "../../api/impersonation";
import { AccessibilityProvider } from "../../contexts/AccessibilityContext";
import { PermissionProvider } from "../../store/PermissionContext";
import { WorkspaceProvider } from "../../store/WorkspaceContext";
import { ErrorBoundary } from "../../store/ErrorBoundary";
import { LoginPage } from "../../components/shared/LoginPage";
import { AppFooter, type AppFooterState } from "../../components/shared/AppFooter";
import { useLocale } from "../../i18n/I18nProvider";
import AppLayout from "../../components/workspace/AppLayout";
import { workspaceRoutes } from "./routes";
import { ToastProvider } from "../../components/shared/ToastProvider";
import { createQueryClient } from "../../utils/queryClient";
import { ReleaseFeaturesGate } from "../../feature-gate";
import { usePermissionContext } from "../../store/PermissionContext";
import { EmptyState } from "../../components/shared/EmptyState";

// ---------------------------------------------------------------------------
// TanStack Query client (Contract 9: Data Fetching)
// ---------------------------------------------------------------------------

const queryClient = createQueryClient();

// ---------------------------------------------------------------------------
// Login route — redirects to previous page after successful login
// ---------------------------------------------------------------------------

export function WorkspaceLoginRoute(): React.ReactElement {
  const { authenticated } = useAuth();
  const location = useLocation();
  // PRE-AUTH read: BrandingProvider fetched the record with a public,
  // unauthenticated request, so the legal footer renders before any principal
  // exists. This route element IS the /login entry of the RULE-006a mount table.
  const { branding, state } = useBranding();
  const { locale } = useLocale();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  // D-BR-17: the provider's three states map onto AppFooter's three here, at the
  // mount. `loading` becomes `hidden` - nothing is known yet, so nothing is
  // claimed - and `unavailable` stays the fail-VISIBLE line of RULE-001.
  const footerState: AppFooterState = state === "loading" ? "hidden" : state;

  if (authenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <LoginPage
      // The app SUFFIX, the same Lingui string this app already passes to its
      // BrandingProvider at :208, so the heading and document.title are composed
      // from one pair of values (D-BR-08, RULE-012). The last vendor literal on
      // this line - TASK-PB-BRAND-WORKSPACE-SHELL removed its twin one line from
      // here - leaves the tree with this change.
      appSuffix={t`Workspace`}
      footer={
        <AppFooter values={branding.legal_footer} state={footerState} locale={locale} />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Legal footer — the workspace application shell mount of RULE-006a
// ---------------------------------------------------------------------------

/**
 * Map the BrandingProvider's read state onto AppFooter's render state.
 *
 * All three provider states are mapped DELIBERATELY, one arm each (D-BR-17):
 * - `loading` -> `hidden`: nothing has been read yet, so nothing is claimed —
 *   neither a legal notice nor the statement that one is missing. The provider's
 *   3 s AbortController bound makes this state temporary by construction, so no
 *   route can sit in it and quietly never show the notice.
 * - `ok` -> `ok`: AppFooter itself renders nothing when `enabled` is false or
 *   every field resolves empty (RULE-006b, SCENARIO-002).
 * - `unavailable` -> `unavailable`: the legal footer fails VISIBLE (RULE-001,
 *   D-BR-15). A bar that vanished because the read failed is a violation, which
 *   is why this arm is a rendered line and not a `hidden`.
 *
 * The switch is exhaustive and carries no `default`, so a fourth provider state
 * would be a compile error here rather than a silently dropped footer.
 *
 * Named `mapFooterState` and not `footerState` (the spelling the tenant app uses
 * for the same helper) only because `WorkspaceLoginRoute` above already holds a
 * local `footerState` const; a module-scope function shadowed inside the same
 * file would read as the same thing and be a different one.
 *
 * @see SPEC-PLATFORM-BRANDING-RULE-001 / RULE-006
 * @see SPEC-PLATFORM-BRANDING-UI-003
 */
function mapFooterState(state: BrandingState): AppFooterState {
  switch (state) {
    case "ok":
      return "ok";
    case "unavailable":
      return "unavailable";
    case "loading":
      return "hidden";
  }
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function WorkspaceLayout(): React.ReactElement {
  const routeElement = useRoutes([
    {
      path: "*",
      children: workspaceRoutes,
    },
  ]);
  // The footer values come from the ONE branding reader mounted at the SPA root
  // (UI-004); this layout neither fetches them nor composes a per-workspace
  // variant — one operator footer serves every workspace, and no workspace id,
  // tenant id or member role reaches AppFooter (D-BR-09).
  const { branding, state: brandingState } = useBranding();
  const { locale } = useLocale();

  return (
    // THE FOOTER IS A PROP NOW, NOT A CHILD (RULE-020, D-BR-29, SCENARIO-032).
    // A child lands inside AppLayout's padded content wrapper, which is INSIDE
    // the scrolling <main> and BESIDE the navigation - the placement D-BR-20 and
    // D-BR-26 ruled and D-BR-29 overturned. The `footer` prop renders the bar as
    // the LAST child of the shell's full-height column, under both the
    // navigation and the content. The prop is OPTIONAL and this is its only
    // consumer, so the shell's other consumers render exactly as they did.
    <AppLayout
      footer={
        <AppFooter
          values={branding.legal_footer}
          state={mapFooterState(brandingState)}
          locale={locale}
        />
      }
    >
      {/* The routed region still fills the padded content wrapper: a page root
          that sizes itself with `flex: 1; minHeight: 0` (the workspace chat)
          only fills the region when its parent is a flex container
          (TASK-PB-FOOTER-AUDIT-FIXES). That is why this wrapper stays even
          though the bar it once had to push down has left the column. */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{routeElement}</Box>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Impersonation mount (SPEC-FRONTEND-RULE-095)
// ---------------------------------------------------------------------------

/**
 * MOUNT POINT 2 OF 3 of the identity-keyed remount - the one an earlier draft
 * missed (audit r1 P1-07). This SPA's AuthWrapper holds NO PermissionProvider,
 * so the key goes around PermissionProvider INSIDE WorkspaceAuthWrapper's
 * children and NEVER around the wrapper itself: keying the wrapper would
 * remount AuthProvider, restart the OIDC bootstrap and loop the arrival.
 */
export function WorkspaceIdentityBoundary({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const identityKey = impersonationIdentityKey(useAuth().impersonation ?? null);
  return <React.Fragment key={identityKey}>{children}</React.Fragment>;
}

/**
 * Inside a binding a failed permissions fetch is a LOUD error with retry,
 * rendered before any gated UI (SPEC-FRONTEND-RULE-095(8)); outside one it is
 * transparent.
 */
function ImpersonatedPermissionsGuard({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { impersonatedFailure, refetchPermissions } = usePermissionContext();
  if (impersonatedFailure) {
    return (
      <EmptyState
        variant="error"
        title={t`The viewed user's permissions could not be loaded`}
        description={t`Nothing is shown until they load, so that no screen appears with the wrong permissions.`}
        actionText={t`Retry`}
        onAction={refetchPermissions}
      />
    );
  }
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

/**
 * Workspace SPA root component.
 *
 * Provides the authentication wrapper, workspace context, MUI theme,
 * and responsive layout shell. All workspace routes are rendered within
 * this component tree.
 */
/**
 * The shared theme provider with the installation brand colours applied
 * (SPEC-PLATFORM-BRANDING RULE-007).
 *
 * A separate component because the palette is CONTEXT the BrandingProvider
 * publishes: `useBranding()` has to be called BELOW that provider, and
 * `WorkspaceApp` is the component that renders it. The subtree arrives as
 * `children`, so the login route and the layout mount exactly where they did.
 *
 * Still NO `defaultMode`: it falls through to ThemeContext's own "system" so the
 * SPA honours prefers-color-scheme, per STORY-FRONTEND-THEMING AC-001. Deleting
 * the prop is preferred to writing defaultMode="system" — one default, in one
 * place, cannot drift.
 */
function BrandedThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { brandPalette } = useBranding();
  return <VerisaThemeProvider brandPalette={brandPalette}>{children}</VerisaThemeProvider>;
}

export function WorkspaceApp(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      {/* The one reader of GET /api/v1/branding, ABOVE VerisaThemeProvider
          (SPEC-PLATFORM-BRANDING UI-004). A plain fetch, so it neither uses
          nor waits for the query client or the release-toggle read.

          THE SUFFIX, NEVER A PRODUCT NAME (D-BR-08, RULE-012). The mount owns
          this app's title suffix and passes it as a Lingui string; the provider
          composes `"<product_name> - <suffix>"` with an ASCII hyphen (RULE-064)
          the moment the read lands, so a branded installation reads
          "Varosi AI - Munkaterület" and nothing here carries the vendor name.
          The macro is evaluated in render and `I18nProvider` remounts this
          subtree on a locale switch (`key={locale}`), so the title follows the
          UI language. */}
      <BrandingProvider appSuffix={t`Workspace`}>
        <BrandedThemeProvider>
          <ErrorBoundary>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AccessibilityProvider agentId="app">
                <WorkspaceAuthWrapper>
                  {/* The banner mounts ABOVE ReleaseFeaturesGate and every
                      other gate (SPEC-FRONTEND-RULE-095(1)). */}
                  <ImpersonationBannerSlot />
                  <WorkspaceIdentityBoundary>
                    <PermissionProvider>
                      <ToastProvider>
                        <ImpersonatedPermissionsGuard>
                          <ReleaseFeaturesGate>
                            <Routes>
                              <Route path="/login" element={<WorkspaceLoginRoute />} />
                              <Route
                                path="/*"
                                element={
                                  <WorkspaceProvider>
                                    <WorkspaceLayout />
                                  </WorkspaceProvider>
                                }
                              />
                            </Routes>
                          </ReleaseFeaturesGate>
                        </ImpersonatedPermissionsGuard>
                      </ToastProvider>
                    </PermissionProvider>
                  </WorkspaceIdentityBoundary>
                </WorkspaceAuthWrapper>
              </AccessibilityProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </BrandedThemeProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
}

export default WorkspaceApp;
